import { parseSSE, STREAM_RETRY_DELAYS_MS } from "@sagot/chat";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { API_HEADERS } from "@sagot/constants";
import { getActiveWorkspaceId } from "../../../app/api";
import { useClient } from "../../../session/session-provider";

/** `event: inbox` payload published on the tenant channel (backend `services/inbox/events.py`). */
const inboxEvent = z.object({
  type: z.string(),
  conversation_id: z.string(),
  at: z.string().optional(),
  data: z.record(z.unknown()).optional(),
});

export type InboxStreamEvent = z.infer<typeof inboxEvent>;
export type StreamState = "connecting" | "live" | "reconnecting";

const INBOX_EVENT_NAME = "inbox";

export function decodeInboxEvent(name: string, raw: string): InboxStreamEvent | null {
  if (name !== INBOX_EVENT_NAME) return null;
  try {
    const parsed = inboxEvent.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    }, { once: true });
  });

/**
 * Real-time inbox updates over SSE-over-fetch (`GET /v1/inbox/events`, bearer auth), with
 * backoff reconnects. Callers fall back to polling while the state is not `live`. With
 * `enabled = false` (the member cannot view the inbox) no connection is made.
 */
export function useInboxStream(onEvent: (event: InboxStreamEvent) => void, enabled = true): StreamState {
  const client = useClient();
  const [state, setState] = useState<StreamState>("connecting");
  const handler = useRef(onEvent);
  useEffect(() => {
    handler.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    const stopped = () => signal.aborted;
    /**
     * Direct fetch (not the API client): a long-lived stream must not inherit the client's
     * request timeout, and aborting must cancel the body as well as the request.
     */
    async function connectOnce(): Promise<void> {
      const token = await client.session.ensureFresh();
      const workspaceId = getActiveWorkspaceId();
      const headers = new Headers({ accept: "text/event-stream" });
      if (token) headers.set(API_HEADERS.authorization, `Bearer ${token.value}`);
      if (workspaceId) headers.set(API_HEADERS.workspace, workspaceId);
      const response = await fetch(`${client.baseUrl}/v1/inbox/events`, { headers, credentials: "include", signal });
      if (!response.ok || !response.body) throw new Error("inbox stream unavailable");
      setState("live");
      for await (const message of parseSSE(response.body, { signal })) {
        const event = decodeInboxEvent(message.event, message.data);
        if (event) handler.current(event);
      }
    }
    async function run(): Promise<void> {
      let failures = 0;
      while (!stopped()) {
        try {
          await connectOnce();
          failures = 0;
        } catch {
          failures += 1;
        }
        if (stopped()) return;
        setState("reconnecting");
        await wait(STREAM_RETRY_DELAYS_MS[Math.min(failures, STREAM_RETRY_DELAYS_MS.length - 1)] ?? 10_000, signal);
      }
    }
    void run();
    return () => {
      controller.abort();
    };
  }, [client, enabled]);

  return state;
}
